import React from 'react';
import { Box, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';

const shimmer = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

/**
 * ShimmerSkeleton — single shimmer bar. Compose multiples for table rows.
 *
 * Props:
 *   width   — string|number
 *   height  — string|number
 *   sx      — MUI sx override
 */
export function ShimmerBar({ width = '100%', height = 16, sx = {} }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width,
        height,
        borderRadius: 1,
        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.07)',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark
            ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)'
            : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
          animation: `${shimmer} 1.4s infinite`,
        },
        ...sx,
      }}
    />
  );
}

/**
 * ShimmerTableRows — renders `count` shimmer table rows with `cols` columns.
 */
export function ShimmerTableRows({ count = 5, cols = 5 }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <>
      {Array.from({ length: count }).map((_, ri) => (
        <tr key={ri}>
          {Array.from({ length: cols }).map((_, ci) => (
            <td
              key={ci}
              style={{
                padding: '14px 16px',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.06)'}`,
              }}
            >
              <ShimmerBar
                width={ci === 0 ? '80%' : ci === cols - 1 ? '50%' : `${60 + (ci * 7) % 30}%`}
                height={14}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default ShimmerBar;
